import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import { inputStyles } from 'assets/components/inputs/Input';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import { flatten, isEmpty, uniqBy } from 'lodash';
import useClaimFilters from 'module/claims/hooks/useClaimFilters';
import useClaimStatuses from 'module/claims/hooks/useClaimStatuses';
import { useClaimProvider } from '../Claim.Hooks';
import { ClaimSwitchListButton } from '../other/Claim.SwitchListButton';

export default function ClaimFilters() {
  const { lang } = getLocales();
  const { listApi: claimListApi, dealersApi, userApi, vehicleMakeApi } = useClaimProvider();
  const { claimStatuses, claimJobStatuses } = useClaimStatuses();
  const { userId, permissions } = useAuthStore();
  const { defaultFilters } = useClaimFilters();

  const WITHOUT_OWNER = -1;
  const withoutOwnerData = {
    id: WITHOUT_OWNER,
    title: 'N / A',
    media: null,
    icon: <Icon class="fas-user" />,
  };

  return (
    <FilterForm
      subcomponents={ThemeFilterFormDrawer}
      headerActions={<ClaimSwitchListButton />}
      search={claimListApi?.body?.search}
      onSearch={(search) => claimListApi?.execute((body) => ({ ...body, search }))}
      filters={claimListApi?.body}
      onFilter={(newFilters) => {
        if (newFilters.ownerId?.includes(WITHOUT_OWNER)) {
          newFilters = {
            ...newFilters,
            ownerId: newFilters.ownerId.filter((id) => id !== WITHOUT_OWNER),
            includeOwnerless: true,
          };
        }

        claimListApi?.execute(newFilters);
      }}
      onReset={() => claimListApi.execute(defaultFilters)}
      onOpen={() => {
        if (!vehicleMakeApi?.payload) vehicleMakeApi.execute((b) => b);
        if (!userApi?.payload) userApi.execute((b) => b);
        if (!dealersApi?.payload) dealersApi.execute((b) => b);
      }}
      render={({ tempFilters, setFilters }) => {
        const dealers = dealersApi?.payload?.data ?? [];

        const products = uniqBy(
          !isEmpty(tempFilters?.dealerId)
            ? flatten(dealers.filter((d) => tempFilters?.dealerId?.some((id) => id === d.id)).map((d) => d.products))
            : flatten(dealers.map((d) => d.products)),
          (p) => p.id
        );

        return (
          <>
            <SelectInput
              name="statusCode"
              label={lang.status}
              data={claimStatuses}
              isMultiselect
              value={tempFilters?.statusCode}
              onChange={setFilters}
              icon={<Icon class="fas-check" />}
            />
            <SelectInput
              name="claimJobStatusCode"
              label={lang.claimJobStatus}
              data={claimJobStatuses}
              isMultiselect
              value={tempFilters?.claimJobStatusCode}
              onChange={setFilters}
              icon={<Icon class="fas-check" />}
            />
            <SelectInput
              name="makeId"
              label={lang.vehicleMakes}
              data={vehicleMakeApi?.payload?.data?.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-car" />,
              }))}
              isMultiselect
              value={tempFilters?.makeId}
              onChange={setFilters}
              icon={<Icon class="fas-car" />}
            />
            <SelectInput
              name="productId"
              label={lang.products}
              data={products?.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-box" />,
              }))}
              isMultiselect
              value={tempFilters?.productId}
              onChange={setFilters}
              icon={<Icon class="fas-box" />}
            />
            <SelectInput
              name="dealerId"
              label={lang.dealers}
              data={dealers?.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-user-friends" />,
              }))}
              isMultiselect
              value={tempFilters?.dealerId}
              onChange={setFilters}
              icon={<Icon class="fas-user-friends" />}
            />
            <SelectInput
              name="ownerId"
              label={lang.owner}
              data={[
                withoutOwnerData,
                ...(userApi?.payload?.data?.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-user" />,
                })) || []),
              ]}
              isMultiselect
              value={[...(tempFilters?.ownerId || []), ...(tempFilters?.includeOwnerless ? [WITHOUT_OWNER] : [])]}
              onChange={({ ownerId }) => setFilters({ ownerId, includeOwnerless: ownerId.includes(WITHOUT_OWNER) })}
              icon={<Icon class="fas-user" />}
            />
            <DateInput
              name="createdAtFromDateUtc"
              label={lang.createdAtFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.createdAtFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="createdAtToDateUtc"
              label={lang.createdAtToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.createdAtToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            {!permissions.VIEW_ONLY_OWNED_CLAIMS && (
              <CheckInput
                name="showMyClaims"
                label={lang.showMyClaims}
                class={inputStyles.check.switch + ' formInput'}
                style={{ marginTop: Theme.Size.M, alignSelf: 'center' }}
                value={tempFilters?.showMyClaims}
                onChange={() =>
                  setFilters({
                    reporterId: !tempFilters?.showMyClaims ? userId : null,
                    showMyClaims: !tempFilters?.showMyClaims,
                  })
                }
                htmlElementProps={{ title: tempFilters.showMyClaims ? lang.disable : lang.enable }}
              />
            )}
          </>
        );
      }}
    />
  );
}
